import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchAttendanceDay, fetchCorrections } from '../api/client';
import { fetchOrgRoster } from '../api/hbs';
import type { AttendanceEvent } from '../api/types';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { EventMap } from '../components/EventMap';
import { calendarDateInZone } from '../lib/attendance-history';
import { attendanceMapData } from '../lib/attendance-map';
import { approvedHalfDayLeaveByDate, formatHalfDayPart } from '../lib/correction-form';
import { dayDetailChrome } from '../lib/day-detail-chrome';
import { displayNameOf } from '../lib/display-name';
import {
  formatAccuracy,
  formatDateInZone,
  formatDayStatus,
  formatDistance,
  formatLateMinutes,
  formatTimeInZone,
  formatVerification,
  formatWorkMode,
  isUnverified,
} from '../lib/format';
import { EmptyState, ErrorCard, LoadingState } from '../states';

/**
 * Peta hari ini — hanya muncul bila ada yang dapat digambar.
 *
 * Diadaptasi dari `PetaAdmin` aplikasi absensi-kurir Emguard, tetapi menggambar TITIK, bukan
 * jejak: Workforce tidak punya ping posisi berkala, dan garis lurus antara lokasi check-in dan
 * check-out adalah rute yang tidak pernah diukur.
 *
 * Jumlah event yang tidak dapat digambar DINYATAKAN, tidak dibuang diam-diam. Hari yang GPS-nya
 * mati akan terlihat sama persis dengan hari tanpa absen bila angka itu disembunyikan, dan
 * keduanya menuntut tindakan berbeda dari penyelia.
 */
function DayMap({
  events,
  timezone,
}: {
  readonly events: readonly AttendanceEvent[];
  readonly timezone: string;
}): ReactNode {
  /*
   * `useMemo` bukan demi kecepatan — `attendanceMapData` murah. Ia menjaga IDENTITAS `points`.
   *
   * `EventMap` menggambar ulang lewat `useEffect([points])` dan menutupnya dengan `fitBounds`.
   * Tanpa memo, array baru lahir pada SETIAP render, jadi efek itu menembak walau datanya sama
   * persis — dan `AuthBoundary` merakit objek sesi baru tiap render lalu meneruskannya ke context
   * tanpa memo, sehingga penyegaran token Supabase me-render ulang seluruh pohon ini.
   *
   * Akibatnya di tangan penyelia: ia memperbesar peta ke satu titik, pindah ke WhatsApp untuk
   * menanyai kurirnya, kembali — dan petanya sudah mundur ke tampilan awal. Cacat sejenis sudah
   * ditemukan dan diperbaiki pada `TaskDetailPage` (#750); di sana pemicunya `watchPosition`, di
   * sini lebih jarang tetapi kegagalannya sama.
   */
  const { points, withoutCoordinates, superseded } = useMemo(
    () => attendanceMapData(events),
    [events],
  );
  if (points.length === 0 && withoutCoordinates === 0 && superseded === 0) return null;

  return (
    <section className="card">
      <h3>Peta</h3>
      {points.length > 0 ? (
        <EventMap points={points} timeLabel={(iso) => formatTimeInZone(iso, timezone)} />
      ) : (
        <p className="muted small">
          Tidak ada event hari ini yang punya koordinat yang dapat digambar.
        </p>
      )}
      {withoutCoordinates > 0 ? (
        <p className="muted small">
          {withoutCoordinates} event tanpa koordinat tidak tergambar — lokasi tidak terekam saat
          absen, biasanya karena izin lokasi ditolak atau sinyal GPS tidak tersedia.
        </p>
      ) : null}
      {superseded > 0 ? (
        <p className="muted small">
          {superseded} event yang sudah digantikan koreksi tidak tergambar. Baris aslinya tetap ada
          pada daftar event di bawah.
        </p>
      ) : null}
      {points.length > 0 ? (
        <p className="muted small">
          Lingkaran samar di sekitar tiap titik adalah ketidakpastian yang dilaporkan perangkat,
          bukan radius geofence. Peta ini menampilkan tempat absen ditekan — bukan rute perjalanan.
        </p>
      ) : null}
    </section>
  );
}

/**
 * Detail satu hari — SELURUH event mentah, bukan ringkasan.
 *
 * Ringkasan menjawab "berapa jam saya kerja"; event mentah menjawab "mengapa hari ini dianggap
 * belum lengkap". Pertanyaan kedua yang membawa orang ke halaman ini, dan yang menentukan
 * apakah ia perlu mengajukan koreksi.
 */
export function DayDetailPage(): ReactNode {
  const session = useSession();
  /*
   * Dua rute memakai layar ini: `history/:workDate` (hari SENDIRI) dan
   * `history/:userId/:workDate` (hari ORANG LAIN — layar penyelia, 23 Agu 2026).
   *
   * ADA KARENA peta absen dan alasan di luar radius dibangun untuk mata PENYELIA, dan sampai
   * hari ini penyelialah satu-satunya yang tidak dapat melihatnya: rute detail hari memaku
   * `userId` ke diri sendiri, dan dashboard tidak menautkan ke mana pun.
   */
  const { workDate = '', userId } = useParams<{ workDate: string; userId?: string }>();
  const subjekUserId = userId ?? session.me.userId;
  /*
   * Bingkainya datang dari fungsi murni, bukan dihitung di sini — karena salah satu keputusannya
   * (boleh-tidaknya menawarkan "Ajukan koreksi") bersifat keselamatan, dan aturan semacam itu
   * tidak boleh hidup sebagai `? :` di tengah JSX tempat ia dapat lenyap tanpa ada yang merah.
   */
  const bingkai = dayDetailChrome(subjekUserId, session.me.userId);
  const milikOrangLain = bingkai.milikOrangLain;
  const timezone = session.policy?.timezone ?? 'Asia/Jakarta';

  const { state, reload } = useAsync(
    () => fetchAttendanceDay(session.auth, subjekUserId, workDate),
    [session.auth, subjekUserId, workDate],
  );

  /*
   * Nama subjek — hanya dicari saat membuka hari orang lain.
   *
   * Menampilkan hari seseorang TANPA menyebut namanya adalah cacat yang mahal di layar ini:
   * penyelia membacanya untuk memutuskan, dan keputusan yang tertuju pada orang yang keliru
   * lebih buruk daripada tidak ada layar sama sekali. Karena itu ketidaktahuan DINYATAKAN,
   * bukan disembunyikan — bukan pula diganti UUID, yang tak berarti apa pun bagi pembacanya.
   *
   * Kegagalannya tidak berbunyi sebagai kartu galat: rincian harinya tetap sah dan berguna.
   */
  const [namaSubjek, setNamaSubjek] = useState<string | null | 'memuat'>(
    milikOrangLain ? 'memuat' : null,
  );
  useEffect(() => {
    if (!milikOrangLain) return;
    let aktif = true;
    setNamaSubjek('memuat');
    fetchOrgRoster(session.auth.accessToken, session.me.organizationId)
      .then((roster) => {
        if (aktif) setNamaSubjek(displayNameOf(roster, subjekUserId));
      })
      .catch(() => {
        if (aktif) setNamaSubjek(null);
      });
    return () => {
      aktif = false;
    };
  }, [milikOrangLain, session.auth.accessToken, session.me.organizationId, subjekUserId]);

  /*
   * Keterangan izin setengah hari — MALAS dan SENYAP saat gagal, alasan sama dengan HistoryPage.
   *
   * `fetchCorrections` mengembalikan seluruh yang TERLIHAT bagi pelaku (dirinya atau timnya),
   * bukan hanya milik `subjekUserId` — disaring di sini SEBELUM dicari berdasarkan tanggal, agar
   * hari orang lain tidak pernah kebetulan menampilkan izin setengah hari milik orang ketiga.
   */
  const [bagianSetengahHari, setBagianSetengahHari] = useState<string | null>(null);
  useEffect(() => {
    let aktif = true;
    setBagianSetengahHari(null);
    fetchCorrections(session.auth)
      .then((koreksi) => {
        if (!aktif) return;
        const milikSubjek = koreksi.filter((k) => k.subjectUserId === subjekUserId);
        setBagianSetengahHari(approvedHalfDayLeaveByDate(milikSubjek).get(workDate) ?? null);
      })
      .catch(() => {
        /* senyap — lihat alasannya di HistoryPage */
      });
    return () => {
      aktif = false;
    };
  }, [session.auth, subjekUserId, workDate]);

  return (
    <>
      <section className="card">
        <div className="row space-between">
          <Link to={bingkai.kembaliKe}>{bingkai.labelKembali}</Link>
          {/*
            "Ajukan koreksi" SENGAJA hilang saat membuka hari orang lain.
            `CorrectionNewPage` memaku `subjectUserId: session.me.userId`, jadi dari sini tautan
            itu akan mengajukan koreksi pada catatan PENYELIANYA SENDIRI untuk tanggal itu —
            diam-diam, tanpa satu pun galat. Mengoreksi catatan orang lain adalah kemampuan
            tersendiri dengan alur persetujuannya sendiri, bukan tautan yang berbohong soal
            subjeknya.
          */}
          {bingkai.bolehAjukanKoreksi ? (
            <Link to={`/attendance/corrections/new?workDate=${encodeURIComponent(workDate)}`}>
              Ajukan koreksi
            </Link>
          ) : null}
        </div>
        <h2>{formatDateInZone(`${workDate}T00:00:00.000Z`, 'UTC')}</h2>
        {milikOrangLain ? (
          <p className="small">
            <strong>
              {namaSubjek === 'memuat' ? 'Memuat nama…' : (namaSubjek ?? '(nama tidak diketahui)')}
            </strong>{' '}
            — Anda membuka catatan orang lain.
          </p>
        ) : null}
        <p className="muted small">Waktu dalam zona {timezone}.</p>
      </section>

      {state.status === 'loading' ? <LoadingState label="Memuat detail hari…" /> : null}
      {state.status === 'error' ? <ErrorCard error={state.error} onRetry={reload} /> : null}

      {state.status === 'ready' ? (
        <>
          <section className="card">
            <h3>Ringkasan</h3>
            <dl>
              <dt>Status</dt>
              <dd>{formatDayStatus(state.data.day?.status ?? null)}</dd>
              {/*
                Status DI ATAS tetap apa adanya (ketetapan Owner) — ini keterangan di sampingnya,
                bukan pengganti. Baris ini menjelaskan MENGAPA tanpa mengubah fakta status itu.
              */}
              {bagianSetengahHari ? (
                <>
                  <dt>Keterangan</dt>
                  <dd>Izin setengah hari ({formatHalfDayPart(bagianSetengahHari)}) — disetujui</dd>
                </>
              ) : null}
              <dt>Masuk</dt>
              <dd>
                {state.data.day?.firstCheckInAt
                  ? formatTimeInZone(state.data.day.firstCheckInAt, timezone)
                  : 'Tidak ada'}
              </dd>
              <dt>Pulang</dt>
              <dd>
                {state.data.day?.lastCheckOutAt
                  ? formatTimeInZone(state.data.day.lastCheckOutAt, timezone)
                  : 'Tidak ada'}
              </dd>
              <dt>Keterlambatan</dt>
              <dd>{formatLateMinutes(state.data.day?.lateMinutes ?? 0)}</dd>
            </dl>
            <p className="muted small">
              Status ditetapkan server memakai kebijakan yang berlaku saat itu. Perubahan kebijakan
              setelahnya tidak mengubah catatan ini.
            </p>
          </section>

          <DayMap events={state.data.events} timezone={timezone} />

          {state.data.events.length === 0 ? (
            <EmptyState
              title="Tidak ada event pada hari ini"
              hint="Bila Anda sebenarnya bekerja pada hari ini, ajukan koreksi dengan alasannya."
            />
          ) : (
            <section className="card">
              <h3>Event mentah</h3>
              <ul className="list">
                {state.data.events.map((event) => {
                  const eventDate = calendarDateInZone(event.occurredAtServer, timezone);
                  return (
                    <li key={event.id} className="list-item">
                      <div className="row space-between">
                        <strong>
                          {event.eventType === 'CHECK_IN' ? 'Absen masuk' : 'Absen pulang'}{' '}
                          {formatTimeInZone(event.occurredAtServer, timezone)}
                        </strong>
                        <span>{formatWorkMode(event.workMode)}</span>
                      </div>

                      {eventDate !== workDate ? (
                        <p className="muted small">
                          Terjadi pada {eventDate} — sesi melewati tengah malam, tetap tercatat pada
                          hari kerja {workDate}.
                        </p>
                      ) : null}

                      <p
                        className={
                          isUnverified(event.locationVerification) ? 'warning small' : 'small'
                        }
                      >
                        {formatVerification(event.locationVerification)}
                      </p>
                      <p className="muted small">
                        {formatAccuracy(event.accuracyMeters)}
                        {event.distanceMeters !== null
                          ? ` · ${formatDistance(event.distanceMeters)} dari lokasi kerja`
                          : ''}
                      </p>

                      {/*
                        Alasan di luar radius — DI ATAS catatan biasa, dan tidak diredupkan.

                        Ia bukan keterangan tambahan: kebijakan `outsideGeofenceRequiresReason`
                        MEWAJIBKAN pekerja menulisnya, dan inilah satu-satunya hal yang dibaca
                        penyelia untuk memutuskan menyetujui absen di luar radius. Sampai 23 Agu
                        2026 tidak ada satu layar pun yang menampilkannya kembali — jadi kewajiban
                        itu dituntut dari kurir, lalu jawabannya tidak pernah sampai ke mata orang
                        yang membutuhkannya.

                        Diberi label, bukan berdiri telanjang: tanpa "Alasan di luar radius:" ia
                        terbaca sebagai catatan kedua, dan bedanya justru yang menentukan.
                      */}
                      {event.outsideGeofenceReason ? (
                        <p className="small">
                          <strong>Alasan di luar radius:</strong> {event.outsideGeofenceReason}
                        </p>
                      ) : null}

                      {event.note ? <p className="small">Catatan: {event.note}</p> : null}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </>
      ) : null}
    </>
  );
}
